import { ref } from 'vue'
import { sendInfo } from '@/utils/sendError'
import { getOriginalFetch } from '@/utils/fetch-client'

export const serverUnavailable = ref(false)

const FAST_INTERVAL_MS = 2000
const SLOW_INTERVAL_MS = 10000
const FAST_ATTEMPTS = 8

const recoverCallbacks = new Set<() => void>()
const recoverWaiters = new Set<() => void>()
let polling = false
let attempts = 0

// Fires when the server comes back after being marked unavailable. Consumers use
// it to resume work that was held while the server was down (e.g. draining the
// collection sync queue) instead of polling themselves.
export function onServerRecover(callback: () => void): void {
  recoverCallbacks.add(callback)
}

// Resolves the next time the server recovers (or immediately if it is currently
// reachable). Kept for callers that want to await recovery inline.
export function waitForServer(): Promise<void> {
  if (!serverUnavailable.value) return Promise.resolve()
  return new Promise((resolve) => recoverWaiters.add(resolve))
}

function fireRecover(): void {
  serverUnavailable.value = false
  attempts = 0
  const waiters = [...recoverWaiters]
  recoverWaiters.clear()
  for (const resolve of waiters) resolve()
  for (const callback of recoverCallbacks) callback()
}

function scheduleNextPoll(): void {
  const interval = attempts >= FAST_ATTEMPTS ? SLOW_INTERVAL_MS : FAST_INTERVAL_MS
  setTimeout(poll, interval)
}

function poll(): void {
  attempts++
  getOriginalFetch()(import.meta.env.VITE_GRAPHQL_API_URL, { method: 'GET' })
    .then((res) => {
      if (res.status < 500) {
        sendInfo('retry: waitForServer recovered', {
          source: 'waitForServer',
          attempts: String(attempts),
          status: String(res.status),
        })
        polling = false
        fireRecover()
        return
      }
      scheduleNextPoll()
    })
    .catch(() => scheduleNextPoll())
}

// The server is unreachable/erroring. Raise the flag and, unlike a one-shot
// retry, keep polling (fast at first, then slow) until it actually recovers so
// the "please wait" overlay and the offline read/queue paths stay correct for the
// whole outage instead of clearing after a fixed number of attempts.
export function markServerUnavailable(): void {
  serverUnavailable.value = true
  if (polling) return
  polling = true
  attempts = 0
  scheduleNextPoll()
}
